<template>
  <div
    v-if="!reference"
    class="FileUpload"
  >
    <p class="FileUpload__hint">
      {{ label }}
    </p>
    <b-alert
      show
      variant="info"
    >
      {{ $t('fileUpload.fileUploadHint') }}
    </b-alert>
  </div>
  <div
    v-else
    class="FileUpload"
  >
    <p class="FileUpload__hint">
      {{ label }}
    </p>
    <div
      v-if="!locked && !disabled"
      class="upload"
      :class="{ 'upload--active': dragging }"
    >
      <input
        :id="`f-${doctype}`"
        class="upload__input"
        type="file"
        :name="`f-${doctype}`"
        multiple
        :accept="acceptedInputTypes"
        @change="fileInput"
      >
      <label
        class="upload__label upload-label"
        :for="`f-${doctype}`"
        @drop="drop"
        @dragover="dragover"
        @dragenter="dragenter"
        @dragleave="dragleave"
      >
        <span
          v-if="!activeDragFiles"
          class="d-flex flex-column"
        >
          <span class="upload-label__text">{{ $t('processes.fields.upload_drag_files') }}</span>
          <span>
            <span class="textLink">{{ $t('processes.fields.upload_select_files') }}</span>
            <i class="icon fa-fw fal fa-upload ml-2" />
          </span>
        </span>
        <span v-else>{{ activeDragFiles }} {{ $t('processes.fields.files_selected') }}</span>
      </label>
    </div>
    <div
      v-if="!locked && !disabled"
      class="doc-type"
    >
      <button
        class="doc-type__switch"
        @click.prevent="docTypeVisible = !docTypeVisible"
      >
        {{ $t('processes.fields.document_types_allowed') }}

        <i
          v-if="docTypeVisible"
          class="icon fa-fw fal fa-chevron-up"
        />
        <i
          v-else
          class="icon fa-fw fal fa-chevron-down"
        />
      </button>
      <p
        v-if="docTypeVisible"
        class="doc-type__text"
      >
        {{ consiseInputTypes }}
      </p>
    </div>
    <!-- <div
      v-else-if="isLoading && listedFiles.length"
      :class="['file-upload', { isLoading }]"
    >
      <b-spinner
        variant="secondary"
        style="width: 2rem; height: 2rem"
      />
    </div> -->
    <ul
      v-if="!preview"
      class="file-list"
    >
      <li
        v-for="file in listedFiles"
        :key="`file_${file.id}`"
        class="file"
        :class="{
          'file--in-progress': file.progress !== 100 && !file.error,
          'file--error': file.error
        }"
      >
        <div
          v-if="file.progress === 100 && !file.error"
          class="file__info"
        >
          <span>
            <i class="icon fa-fw fal fa-file" />
            {{ file.name }}
          </span>
          <br>
          <button
            v-if="!locked && !disabled"
            class="text-button text-danger"
            @click.prevent="removeFile(file)"
          >
            <i class="icon fa-fw fal fa-trash-can mr-1" /><span>{{ $t('processes.fields.remove_file') }}</span>
          </button>
          <button
            class="text-button"
            @click.prevent="handleDownload({ file })"
          >
            <i class="icon fa-fw fal fa-download mr-1" /><span>{{ $t('processes.fields.download_file') }}</span>
          </button>
        </div>
        <div
          v-else-if="!file.error"
          class="file__info"
        >
          <span>{{ file.name }} wordt geüpload</span>
          <button
            class="text-button"
            @click.prevent="removeFile(file)"
          >
            <i class="icon fa-fw fal fa-trash-can mr-1" /><span class="text-button__label">Annuleren</span>
          </button>
        </div>
        <div
          v-else
          class="file__info"
        >
          <span class="file__error">{{ file.error }}</span>
        </div>
        <div class="file__progress">
          <div
            v-if="file.progress !== 100 && !file.error"
            class="file__progress-bar"
            :style="{ width: file.progress + '%' }"
          />
        </div>
      </li>
    </ul>
    <ul
      v-else
      class="file-list-preview"
    >
      <li
        v-for="file in listedFiles"
        :key="`file_${file.id}`"
        class="preview"
        :class="{ wide: wide }"
      >
        <div v-if="file.loaded">
          <b-aspect aspect="16:9">
            <img :src="file.url">
          </b-aspect>

          <div class="btns">
            <button
              v-if="!locked && !disabled"
              class="text-button text-danger"
              @click.prevent="removeFile(file)"
            >
              <i class="icon fa-fw fal fa-trash-can mr-1" /><span>{{ $t('processes.fields.remove_file') }}</span>
            </button>
            <button
              class="text-button"
              @click.prevent="handleDownload({ file })"
            >
              <i class="icon fa-fw fal fa-download mr-1" /><span>{{ $t('processes.fields.download_file') }}</span>
            </button>
          </div>
        </div>

        <div v-else>
          <b-aspect aspect="16:9">
            <img
              src="data:image/gif;base64,R0lGODdhpwBTAJEAALS0tLS0tO7u7gAAACwAAAAApwBTAAACt4SPqcvtD6OctNqLs968+w+G4kiW5omm6sq27gvH8kzX9o3n+s73/g8MCofEovGITCqXzKbzCY1Kp9Sq9YrNarfcrvcLDovH5LL5jE6r1+y2+w2Py+emgP2eCSjudrpB74cDGGgzSEhjeJB4mLJ44ugYZxg5Qcn4MJlgObKp1tnxSRh6ScKXOFoBiUrK2ur6ChsrO0tba3uLm6u7y9vr+wscLDxMXGx8jJysvMzc7PwMHS09TR1SAAA7"
            >
          </b-aspect>
          {{ $t('fileUpload.oneMoment') }}
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import { getDownloadURL } from '@/services/aws';
import { saveAs } from 'file-saver';
import { getProcessByUuid, updateProcess } from '@/services/processes'
import Process from '@/models/Process';
import { mapGetters } from 'vuex'
import imageCompression from 'browser-image-compression'

export default {
  name: 'FileUpload',
  props: {
    label: {
      type: String,
      default: ''
    },
    hint: {
      type: String,
      default: ''
    },
    doctype: {
      type: String,
      default: ''
    },
    uuid: {
      type: String,
      required: true,
      default: ''
    },
    files: {
      type: Array,
      default: () => []
    },
    /**
     * The minimal file id is provided separately because this counter spans multiple location versions
     */
    minFileId: {
      type: Number,
      required: true
    },

    // Only available to existing record...
    reference: {
      type: String,
      default: null
    },
    // Only available to existing record...
    casefull: {
      type: String,
      default: null
    },

    disabled: {
      type: Boolean,
      default: false
    },
    locked: {
      type: Boolean,
      default: false
    },

    /**
     * Whether to render a preview of the uploaded image
     */
    preview: {
      type: Boolean,
      default: false
    },
    wide: {
      type: Boolean,
      default: false
    },
    collectionType: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      internalFiles: [],
      listedFiles: [],
      dragging: false,
      activeDragFiles: null,
      dragEnterTarget: null,
      counter: 0,
      docTypeVisible: false,
      isLoading: true,
      compressionOptions: {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      },
    };
  },
  computed: {
    ...mapGetters('processes', ['getCurrentProcessType']),
    ...mapGetters('realisation', ['getActiveRecordValues']),

    collectionName() {
      return this.collectionType || 'Processes';
    },
    acceptedInputTypes() {
      return this.preview
        ? ['image/jpg', 'image/jpeg', 'image/png']
        : ['image/jpg', 'image/jpeg', 'image/png', 'application/pdf'];
    },
    consiseInputTypes() {
      const pipe =
        (...cb) =>
          data =>
            cb.reduce((acc, curr) => curr(acc), data);
      const mpr = cb => data => data.map(cb);
      const split = mpr(p => p.split('/'));
      const getLast = mpr(p => p.pop());
      const addDot = mpr(p => '.' + p);
      const flatComma = p => p.reduce((acc, curr) => acc + ', ' + curr);
      const convertInputType = pipe(split, getLast, addDot, flatComma);
      return convertInputType(this.acceptedInputTypes);
    }
  },
  watch: {
    files: {
      deep: true,
      immediate: true,
      handler(prevFiles, newFiles) {
        this.$nextTick(() => {
          const getArrayLength = arr => arr ? arr.map(val => val.id).length : 0

          // const prev = prevFiles && JSON.stringify(prevFiles)
          // const next = newFiles && JSON.stringify(newFiles || [])
          // console.log('__________________')
          // console.log('case', this.casefull)
          // console.log('prev', prev)
          // console.log('next', next)
          // console.log('files', this.files)
          // console.log('doc type', this.doctype)
        if (!newFiles || (getArrayLength(prevFiles) !== getArrayLength(newFiles))) {

          if (this.files?.length) {
            this.setFiles()
          }
        }
        })
      },
    },
  },

  methods: {
    async setFiles () {
      if (!this.files?.length) return;

      this.isLoading = true
      const existingFiles = await Promise.all(
        this.files.map(async file => {
          // If file is only an index //
          if (typeof file === 'number') {
            return await this.setFileByFileNumber(file)
          }

          file.url = await this.getFileUrl({ id: (file?.id || file), file })
          if (!file.name || file.multiTenant) {
            // file.multiTenant => file is in another tenant's bucket => change the name to this caseRef //
            file.name = `${this.casefull}-${this.doctype}-${(file?.id || file)}`
          }
          return file
        })
      ).catch(err => console.log(err))

      if (!existingFiles?.length) return this.isLoading = false

      this.internalFiles = existingFiles.map(file => {
        file.progress = 100
        file.loaded = true
        file.error = null
        return file
      });

      this.listedFiles = this.internalFiles
      this.$emit('files', {
        doctype: this.doctype, // Intentional no replace -alt
        files: this.listedFiles // .map(file => file.id)
      });
      this.isLoading = false
    },

    async setFileByFileNumber (id) {
      return {
        id,
        name: `${this.casefull}-${this.doctype}-${id}`,
        url: await this.getFileUrl({ id }),
        progress: 100,
        loaded: true,
        error: null
      }
    },

    async addFile(f) {
      if (this.locked || this.disabled) return;
      if (!this.isValidFileType(f)) {
        this.$bvToast.toast(`${this.$i18n.t('fileUpload.wrongExtension')}: ${this.consiseInputTypes}`, {
          title: this.$i18n.t('fileUpload.extensionError'),
          variant: 'danger',
          autoHideDelay: 6000,
          toaster: 'b-toaster-top-right',
          solid: true
        });
        return;
      }

      const maxUploadSize = 1024 * 1024 * 4.8; // 4.8MB
      const compressedFile = /image/.test(f.type) ? await imageCompression(f, this.compressionOptions) : f

      if (compressedFile.size > maxUploadSize) {
        this.$bvToast.toast(this.$i18n.t('fileUpload.fileToBig'), {
          title: this.$i18n.t('fileUpload.toBigError'),
          variant: 'danger',
          autoHideDelay: 6000,
          toaster: 'b-toaster-top-right',
          solid: true
        });
        return;
      }

      const formData = new FormData();

      formData.append('type', this.doctype);
      formData.append('file', compressedFile);
      try {
        const api = await fetch(`${process.env.VUE_APP_AWS_API_BASE_URL}/process/realisation/${this.uuid}/files`, {
          method: 'PUT',
          headers: {
            'x-api-key': process.env.VUE_APP_AWS_API_KEY
          },
          body: formData
        });

        if (!api.ok) {
          throw new Error('Upload failed');
        }
        await api.json()
        await this.updateRecord()

      } catch (e) {
        this.$bvToast.toast(this.$i18n.t('fileUpload.generalError'), {
          title: this.$i18n.t('fileUpload.uploadFailed'),
          variant: 'danger',
          autoHideDelay: 10000,
          toaster: 'b-toaster-top-right',
          solid: true
        });
      }
    },

    fileInput(event) {
      const files = event.target.files;
      if (!files) return;
      [...files].map(this.addFile);
    },
    drop(event) {
      if (this.disabled || this.locked) return;

      event.preventDefault();
      const droppedFiles = event.dataTransfer.files;
      this.dragging = false;
      this.activeDragFiles = null;
      if (!droppedFiles) return;
      [...droppedFiles].map(this.addFile);
    },
    isValidFileType(file) {
      return !!this.acceptedInputTypes.find(type => type === file.type)
    },
    dragenter(event) {
      event.preventDefault();
      if (!this.dragEnterTarget) {
        this.dragging = true;
        this.dragEnterTarget = event.target;
        this.activeDragFiles = event.dataTransfer.items.length;
      } else if (event.srcElement !== this.dragEnterTarget) {
        this.counter = 1;
      }
    },
    dragover(event) {
      event.preventDefault();
    },
    dragleave(event) {
      event.preventDefault();
      if (!this.counter && event.srcElement === this.dragEnterTarget) {
        this.dragEnterTarget = null;
        this.dragging = false;
        this.activeDragFiles = 0;
      } else {
        this.counter = 0;
      }
    },
    async removeFile(file) {
      if (this.disabled || this.locked) return;
      //this.$store.dispatch('processes/disableSaveRecord', true)
      this.isLoading = true
      // Ih the file exists in some other bucket (multitenant) => remove the file from the record only //
      // No Api delete call //
      if (file.multiTenant) {
        const process = await this.removeFileReference({ id: file.id })
          .catch(() => {
            this.$store.dispatch('tenant/setToast', {
              vm: this,
              message: this.$i18n.t('fileUpload.generalError'),
              error: true,
              variant: 'danger',
              delay: 100
            });
          })

        if (process) {
          const removedFileIndex = this.listedFiles.findIndex(listedFile => listedFile.id === file.id)
          if (removedFileIndex !== -1) {
            this.listedFiles.splice(removedFileIndex, 1)
          }

          this.isLoading = false

          return this.$store.dispatch('tenant/setToast', {
            vm: this,
            message: this.$i18n.t('fileUpload.docRemoved'),
            error: false,
            delay: 100
          });
        }
      }

      let api;
      if (!file.multiTenant) {
        api = await fetch(`${process.env.VUE_APP_AWS_API_BASE_URL}/process/${this.uuid}/files/${this.doctype}/${file.id}`, {
          method: 'DELETE',
          headers: {
            'x-api-key': process.env.VUE_APP_AWS_API_KEY
          }
        });
      }

      if (api?.status === 404) {
        //this.$store.dispatch('processes/disableSaveRecord', false)

        const process = await this.removeFileReference({ id: file.id })

        if (process) {
          return this.$store.dispatch('tenant/setToast', {
            vm: this,
            message: this.$i18n.t('fileUpload.docRemoved'),
            error: false,
            delay: 100
          });
        }
      }

      if (!api?.ok) {
        //this.$store.dispatch('processes/disableSaveRecord', false)
        return this.$store.dispatch('tenant/setToast', {
          vm: this,
          message: this.$i18n.t('fileUpload.generalError'),
          error: true,
          variant: 'danger',
          delay: 100
        });
      }



      //const response = await api.json()
      //this.$store.dispatch('processes/disableSaveRecord', false)

      if (api?.ok) {
        await this.updateRecord()

        const removedFileIndex = this.listedFiles.findIndex(listedFile => listedFile.id === file.id)
        if (removedFileIndex !== -1) {
          this.listedFiles.splice(removedFileIndex, 1)
        }

        this.isLoading = false

        this.$store.dispatch('tenant/setToast', {
          vm: this,
          message: this.$i18n.t('fileUpload.docRemoved'),
          error: false,
          delay: 100
        });

      }
    },

    async updateRecord (record) {
      const uuid = this.$route.params?.uuid
      if (!uuid) return
      let process = record;

      if (!process) {
        const token = await this.$auth.getTokenSilently();
        process = await getProcessByUuid({ token, uuid })
      }
      const isRealisation = this.getCurrentProcessType === 'realisation'

      if (isRealisation) {
        this.$store.dispatch('realisations/replaceRecord', { record: process })
        this.getActiveRecordValues.Docs = process.Docs
        this.$store.dispatch('realisation/setActiveRecordValues', { values: this.getActiveRecordValues })
        this.$store.dispatch('realisations/updateSelectedRecordDocs', { docs: process.Docs, locationDocs: process.process?.Docs })
      } else {
        this.$store.dispatch('processes/updateSelectedProcessDocs', { docs: process.Docs, locationDocs: process.process?.Docs })
      }
      return process
    },

    getFileUrl: async function ({ id, file }) {
      const response = await getDownloadURL({
        uuid: file.originalUuid || this.uuid,
        index: id,
        doctype: this.doctype,
        multiTenant: file?.multiTenant
      }).catch(err => console.log(err, this.doctype));

      if (response) {
        return response.url
      }

    },

    /**
     * Get temporary download link & start download
     */
    handleDownload: async function ({ file }) {
      const response = await getDownloadURL({
        uuid: file.originalUuid || this.uuid,
        index: file.id,
        doctype: this.doctype,
        multiTenant: file?.multiTenant
      }).catch(e => console.log(e));

      if (!response ) { // 404
        return this.$store.dispatch('tenant/setToast', {
          vm: this,
          message: this.$i18n.t('fileUpload.fileNotFound', { file: file.name }),
          error: true,
          variant: 'danger',
          delay: 100
        });
      }

      // Chrome, FF, Edge
      if (window.browser && window.browser.downloads) {
        window.browser.downloads.download({
          url: response.url
        });
      } else {
        // Safari, etc.
        const res = await fetch(response.url);
        const blob = await res.blob();
        saveAs(blob, file.name);
      }
    },

    async removeFileReference ({ id }) {
      const token = await this.$auth.getTokenSilently();
      const process = await getProcessByUuid({ token, uuid: this.uuid }).catch(e => console.log(e))
      let updatedProcess

      if (process) {
        delete process._id // Cannot make complete record update with immutable _id in payload //
        const docIndex = process.Docs?.[this.doctype]?.findIndex(doc => doc.id === id)

        if (process.Docs?.[this.doctype] && docIndex !== -1) {
          process.Docs[this.doctype].splice(docIndex, 1)
        }
        // Update Docs in "process (current location)" //
        if (process.process.Docs?.[this.doctype]) {
          process.process.Docs[this.doctype] = process.Docs?.[this.doctype]
        }

        updatedProcess = await updateProcess({ token, process, id }).catch(e => console.log(e))
        if (updatedProcess) {
          if (updatedProcess.processType.value === 'realisation') {
            this.updateRecord(updatedProcess)
          } else {
            const record = new Process({ data: updatedProcess, ref: updatedProcess._id });
            this.$store.dispatch('processes/setSelectedProcess', { process: record, copy: true })
          }
        }

      }

      return updatedProcess
    }
  }
};
</script>

<style lang="scss">
.FileUpload {
  max-width: 620px;
  min-width: 70px;
  // margin-bottom: 30px;

  .FileUpload__question {
    margin-bottom: 0;
  }

  .FileUpload__hint {
    margin-bottom: 10px;
  }

  .file-list {
    list-style-type: none;
    margin: 0;
    padding: 0;
  }

  .file {
    margin-bottom: 20px;

    .file__info {
      display: grid;
      grid-template-columns: 1fr auto;
      gap: 10px;
      font-size: 16px;
      //font-weight: 600;
      margin-left: 0;
      margin-bottom: 5px;

      > :first-child {
        display: grid;
        grid-template-columns: auto 1fr;
        align-items: center;
        word-break: break-word;
      }
    }

    .file__progress {
      background-color: $neutral-grey2;
      display: block;
      width: 100%;
      min-height: 2px;
    }

    .file__progress-bar {
      background-color: $neutral-grey3;
      display: block;
      height: 4px;
    }

    &--in-progress {
      .file__progress {
        background-color: $neutral-grey1;
      }
    }

    &--error {
      color: $primary-red;
      fill: $primary-red;

      @include breakpoint('s') {
        .file__info {
          grid-template-columns: 1fr;
        }
      }
    }
  }

  .upload {
    border: 1px solid #9e9d9d;
    text-align: center;
    margin-bottom: 10px;
    height: 100px;
    position: relative;

    .upload__input {
      opacity: 0;
      position: absolute;
      z-index: -1;
      left: 0;
      pointer-events: none;

      &:focus,
      &:active {
        +.upload__label {
          outline: 2px solid $primary-black;
        }
      }
    }

    .upload__label {
      display: flex;
      /* padding: 23px; */
      height: 100%;
      cursor: pointer;
      justify-content: center;
      align-items: center;

      >span {
        display: inline-flex;
        align-items: center;

        >* {
          margin-left: 5px;
        }
      }
    }

    .upload-label {
      @include breakpoint('s') {
        .upload-label__text {
          display: none;
        }
      }
    }

    &.upload--active {
      background-color: $neutral-grey2;
    }

    &:hover {
      .textLink {
        color: $primary-red;
      }
    }

    .textLink {
      color: $primary-darkblue;
      text-decoration: underline;
    }
  }

  .doc-type {
    margin-bottom: 10px;

    .doc-type__switch {
      display: flex;
      align-items: center;
      background-color: transparent;
      font: inherit;
      border: none;
      cursor: pointer;
      font-size: 16px;
      line-height: 22px;
      fill: $primary-darkblue;
      color: $primary-darkblue;

      &:focus,
      &:hover {
        text-decoration: underline;
      }
    }
  }

  .text-button {
    display: flex;
    align-items: center;
    font-family: inherit;
    font-weight: 600;
    font-size: 16px;
    line-height: 22px;
    color: $primary-darkblue;
    fill: $primary-darkblue;
    background-color: transparent;
    border: 0;
    border-radius: 0;
    cursor: pointer;

    .text-button__label {
      @include breakpoint('s') {
        display: none;
      }
    }

    &:hover {
      color: $primary-red;
      fill: $primary-red;
    }

    &:focus,
    &:active {
      outline: 2px solid $primary-black;
    }
  }

  .file-list-preview {
    margin: 0;
    padding: 0;
  }

  .preview {
    list-style: none;
    margin: 0;
    padding: 10px;
    background: $neutral-grey1;
    position: relative;
    display: inline-block;
    width: calc(50% - 5px);
    margin-bottom: 1rem;

    &:nth-child(2n + 2) {
      margin-left: 10px;
    }

    &.wide {
      width: 100%;
      margin-left: 0;

      .btns {
        display: flex;
        justify-content: space-between;

        button {
          display: inline-block;
        }
      }
    }

    .b-aspect-content {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    img {
      display: inline-block;
      margin: auto;
      max-width: 100%;
      max-height: 100%;
    }
  }
}

.isLoading.file-upload {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 250px;
  .spinner-border {
    border-width: 0.15em;
  }
}


</style>
